import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Heading, Column, Loader, Spinner, Tabs, Row, Spacer, Drawer } from '@oliasoft-open-source/react-ui-library';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import translations from 'src/internationalisation/translation-map.json';
import { TemplatesList } from 'views/settings/templates/templates.list';
import { HighLevelForm } from 'views/settings/templates/high-level.form';
import { TemplateDeleteModal } from 'views/settings/templates/template-delete.modal';
import { ActivityModel } from 'views/settings/templates/activity-model/activity-model';
import { CostSetup } from 'views/settings/templates/cost-setup/cost-setup';
import { ImportFromModal } from 'views/settings/user-settings/import-from.modal';
import { getSectionsDataStructure } from 'store/entities/sections-data-structure/sections-data-structure';
import {
  getSectionTypes,
  getOperationCodes,
  showTemplatesFormUpdated,
  addTemplateFromUpload,
  saveTemplates,
  getUserTemplates,
} from 'store/entities/templates/templates';
import {
  selectCompanyTemplates,
  selectIsAddingTemplates,
  selectIsFetchingTemplates,
  selectTemplatesFormVisibility,
} from 'store/entities/templates/selectors';
import { selectUnitByQuantity } from 'store/entities/unit-templates/selectors';
import { QuantitiesTypes } from 'src/enums/unit-templates';
import { convertToKebabCase } from 'client/common/utils/string-utils';

const Templates = ({
  templates,
  entity,
  getOperationCodes,
  companyTemplates,
  getSectionsDataStructure,
  depthUnit,
  ROPUnit,
  durationUnit,
  volumeUnit,
  isAdding,
  isFetching,
  showTemplatesForm,
  getSectionTypes,
  getUserTemplates,
  showTemplatesFormUpdated,
  addTemplateFromUpload,
  saveTemplates,
}) => {
  const { t } = useTranslation();

  const activeTemplate = templates?.find((template) => template?.active);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [templateModalVisible, setTemplateModalVisible] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [showCopyModal, setShowCopyModal] = useState(false);

  useEffect(() => {
    getUserTemplates();
    getOperationCodes();
    getSectionTypes();
    getSectionsDataStructure();
  }, []);

  const onConfirmCopyFromCompanySettings = ({ items }) => {
    const selectedTemplates = companyTemplates
      .filter((template, idx) => items.includes(idx))
      .map((template) => ({ ...template, templateId: uuidv4() }));
    const updatedTemplates = [...templates, ...selectedTemplates];
    setShowCopyModal(false);
    saveTemplates(updatedTemplates, entity);
  };

  const onClickDelete = (id) => {
    setSelectedId(id);
    setTemplateModalVisible(true);
    showTemplatesFormUpdated(false);
  };

  const onClickUpload = (template, companyId) => {
    addTemplateFromUpload?.(templates, template, entity, companyId);
  };
  const [drawerWidth, setDrawerWidth] = useState(300);

  return (
    <>
      <Column scroll spacing={0}>
        {(isAdding || isFetching) && (
          <Loader text={t(translations.fetching)} theme="light" cover>
            <Spinner dark />
          </Loader>
        )}
        <Row spacing={0} height="100%">
          <Drawer width={drawerWidth} closedWidth={50} open button border onResize={(width) => setDrawerWidth(width)}>
            <Column scroll width="250px" borderRight spacing={0}>
              <TemplatesList
                entity={entity}
                templates={templates}
                isLoading={isAdding}
                onClickDelete={onClickDelete}
                saveTemplates={saveTemplates}
                onShowCopyModal={() => setShowCopyModal(true)}
                onClickUpload={onClickUpload}
              />
            </Column>
          </Drawer>
          <Column scroll padding>
            {showTemplatesForm && activeTemplate && (
              <>
                <Heading top>{activeTemplate?.highLevel?.name}</Heading>
                <Tabs
                  testId="templates-tabs"
                  value={activeTabIndex}
                  onChange={(ev) => setActiveTabIndex(ev.target.value)}
                  margin={0}
                  options={[
                    {
                      testId: `templates-tabs-option-${convertToKebabCase(t(translations.settings_highLevelInputs))}`,
                      value: 0,
                      label: t(translations.settings_highLevelInputs),
                    },
                    {
                      testId: `templates-tabs-option-${convertToKebabCase(t(translations.settings_activityModel))}`,
                      value: 1,
                      label: t(translations.settings_activityModel),
                    },
                    {
                      testId: `templates-tabs-option-${convertToKebabCase(t(translations.settings_costSetup))}`,
                      value: 2,
                      label: t(translations.settings_costSetup),
                    },
                  ]}
                />
                {activeTabIndex === 0 && (
                  <>
                    <Spacer height="var(--padding-xs)" />
                    <HighLevelForm
                      entity={entity}
                      activeTemplate={activeTemplate}
                      templates={templates}
                      depthUnit={depthUnit}
                    />
                  </>
                )}
                {activeTabIndex === 1 && (
                  <ActivityModel
                    entity={entity}
                    templates={templates}
                    depthUnit={depthUnit}
                    ROPUnit={ROPUnit}
                    durationUnit={durationUnit}
                  />
                )}
                {activeTabIndex === 2 && (
                  <CostSetup entity={entity} templates={templates} depthUnit={depthUnit} volumeUnit={volumeUnit} />
                )}
              </>
            )}
          </Column>
        </Row>
      </Column>
      {templateModalVisible && (
        <TemplateDeleteModal
          entity={entity}
          templateId={selectedId}
          templates={templates}
          setModalVisible={setTemplateModalVisible}
        />
      )}
      {showCopyModal && (
        <ImportFromModal
          isVisible={showCopyModal}
          onCloseModal={() => setShowCopyModal(false)}
          onConfirmModal={onConfirmCopyFromCompanySettings}
          importType={'TEMPLATES'}
        />
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  companyTemplates: selectCompanyTemplates(state),
  depthUnit: selectUnitByQuantity(state.entities, QuantitiesTypes.Depth),
  ROPUnit: selectUnitByQuantity(state.entities, QuantitiesTypes.ROP),
  durationUnit: selectUnitByQuantity(state.entities, QuantitiesTypes.Duration),
  volumeUnit: selectUnitByQuantity(state.entities, QuantitiesTypes.Volume),
  showTemplatesForm: selectTemplatesFormVisibility(state).showTemplatesForm,
  isAdding: selectIsAddingTemplates(state),
  isFetching: selectIsFetchingTemplates(state),
});

const mapDispatchToProps = {
  getOperationCodes,
  getSectionsDataStructure,
  getSectionTypes,
  addTemplateFromUpload,
  saveTemplates,
  getUserTemplates,
  showTemplatesFormUpdated,
};

const Container = connect(mapStateToProps, mapDispatchToProps)(Templates);

export { Container as Templates };
